import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    intro: "Your privacy matters to us.",
    body: "The information you submit through the SOMEWHERE waitlist is securely stored by Arabian Networking Group and is used solely to:",
    points: [
      "Confirm your waitlist registration.",
      "Notify you about your waitlist status.",
      "Inform you about the launch of SOMEWHERE and relevant product updates."
    ],
    outro: [
      "Your personal information will never be sold, rented, or shared with third parties without your consent, unless required by applicable law.",
      "We take reasonable measures to protect your information and keep it secure."
    ]
  },
  terms: {
    title: "Terms of Use",
    intro: "By joining the SOMEWHERE waitlist, you acknowledge that:",
    body: null,
    points: [
      "Joining the waitlist does not guarantee immediate access to the platform.",
      "Early access invitations will be issued at Arabian Networking Group's discretion.",
      "Complimentary launch benefits, where applicable, are subject to availability and may change without prior notice.",
      "Arabian Networking Group reserves the right to update these terms as the platform evolves."
    ],
    outro: []
  }
};

export default function LegalPage({ type }) {
  const data = CONTENT[type];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type]);

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white font-inter selection:bg-[#C8A24A]/30 selection:text-white relative overflow-hidden">
      {/* Background radial accent glow */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-radial-gradient from-[#C8A24A]/10 to-transparent pointer-events-none rounded-full blur-[120px]" />

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B0B0F]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="legal-brand-home">
            <img src="/ang-logo-gold.png" alt="Arabian Networking Group" className="h-12 w-auto object-contain" />
            <div className="h-9 w-px bg-white/10" />
            <div className="flex flex-col">
              <span className="text-xl tracking-[0.25em] font-medium font-clash gold-text-metallic leading-none">SOMEWHERE</span>
              <span className="text-[9px] uppercase tracking-[0.15em] text-[#B0B0B0] font-medium font-inter mt-1">
                by Arabian Networking Group
              </span>
            </div>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="text-sm text-[#B0B0B0] hover:text-[#C8A24A] transition-colors duration-200 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-20 md:py-28 relative z-10">
        <div className="space-y-3 mb-12">
          <span className="text-xs uppercase tracking-[0.2em] gold-text-metallic font-semibold">Legal</span>
          <h1 className="text-4xl sm:text-5xl font-semibold font-clash text-white tracking-tight" data-testid="legal-title">
            {data.title}
          </h1>
          <div className="gold-accent-line w-24 mt-4" />
        </div>

        <div className="space-y-6 text-[#B0B0B0] leading-relaxed">
          <p className="text-lg text-white/90 font-light">{data.intro}</p>

          {data.body && <p className="text-base font-light">{data.body}</p>}

          <ul className="space-y-3">
            {data.points.map((point, i) => (
              <li key={i} className="flex items-start gap-3 text-base font-light">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C8A24A] flex-shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>

          {data.outro.map((para, i) => (
            <p key={i} className="text-base font-light">{para}</p>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10 bg-black/60 relative z-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/ang-logo-gold.png" alt="Arabian Networking Group" className="h-10 w-auto object-contain" />
            <div className="flex flex-col items-start">
              <span className="text-sm font-semibold tracking-wider font-clash gold-text-metallic">SOMEWHERE</span>
              <span className="text-[9px] uppercase tracking-[0.15em] text-[#B0B0B0] font-medium font-inter">
                by Arabian Networking Group
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs text-[#8A8A8A] hover:text-[#C8A24A] transition-colors duration-200">Privacy Policy</Link>
            <span className="text-[#8A8A8A]/40 text-xs">•</span>
            <Link to="/terms" className="text-xs text-[#8A8A8A] hover:text-[#C8A24A] transition-colors duration-200">Terms of Use</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
